import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { PRODUCTS } from "@/lib/products";
import SanaaMark from "@/components/SanaaMark";
import LangSwitcher from "@/components/LangSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import ProductMark from "@/components/ProductMark";
import { ArrowRight, LogOut, CreditCard } from "lucide-react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();

  const hour = new Date().getHours();
  const greet = hour < 12 ? t("dashboard.hello") : hour < 18 ? t("dashboard.hello") : t("dashboard.hello");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <SanaaMark size={26} />
            <span className="font-display tracking-tight">Sanaa AI</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitcher />
            <ThemeToggle />
            <button
              data-testid="dashboard-billing"
              onClick={() => nav("/app/billing")}
              className="hidden sm:inline-flex h-9 items-center gap-2 rounded-sm border border-border/60 bg-card/40 hover:bg-card px-3 text-sm"
            >
              <CreditCard className="h-4 w-4" /> Billing
            </button>
            <div className="hidden sm:flex items-center gap-2 ms-2">
              <div className="h-8 w-8 rounded-sm bg-foreground text-background flex items-center justify-center font-medium text-sm">
                {user?.name?.[0] || "U"}
              </div>
              <div className="text-xs leading-tight">
                <div className="text-foreground font-medium">{user?.name}</div>
                <div className="text-muted-foreground capitalize">{user?.role}</div>
              </div>
            </div>
            <button
              data-testid="dashboard-logout"
              onClick={() => {
                logout();
                nav("/");
              }}
              aria-label="Logout"
              className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-border/60 bg-card/40 hover:bg-card"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="relative">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none stripe-mask" />
        <div className="relative mx-auto max-w-7xl px-5 pt-16 pb-24">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
            {greet}, {user?.name?.split(" ")[0]}
          </div>
          <h1 className="font-serif text-5xl sm:text-7xl tracking-tight leading-[1]" data-testid="dashboard-title">
            {t("dashboard.pick")}
          </h1>
          <p className="mt-5 text-muted-foreground text-lg max-w-2xl">{t("dashboard.sub")}</p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRODUCTS.map((p, i) => (
              <Link
                key={p.key}
                to={p.route}
                data-testid={`dashboard-product-${p.key}`}
                className="group relative overflow-hidden rounded-sm border border-border/60 bg-card p-7 transition-all duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${i * 0.06}s` }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 0 0 1px ${p.accent}, 0 30px 80px -25px ${p.accent}55`)}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "")}
              >
                <div className="absolute -top-20 -end-20 h-48 w-48 rounded-full opacity-0 group-hover:opacity-20 transition-opacity blur-3xl" style={{ background: p.accent }} />
                <div className="relative">
                  <ProductMark product={p} size={48} />
                  <h3 className="mt-7 font-display text-2xl tracking-tight">{p.name}</h3>
                  <p className="mt-1 text-sm" style={{ color: p.accent }}>
                    {p.tagline[lang] || p.tagline.en}
                  </p>
                  <p className="mt-4 text-sm text-muted-foreground line-clamp-3">{p.blurb[lang] || p.blurb.en}</p>
                  <div className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: p.accent }}>
                    {t("dashboard.launch")}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
